import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Panel,
  PanelType,
  Dropdown,
  Dialog,
  DialogType,
  DialogFooter,
  MessageBar,
  MessageBarType,
  Separator,
  Spinner,
  mergeStyleSets,
  Persona,
  ActionButton,
  Facepile,
  PersonaSize,
  OverflowButtonType,
  PersonaCoin,
} from "@fluentui/react";
import { Link } from 'react-router-dom';
import TagManager from '../components/TagManager';
import PeopleManager from "../components/PeopleManager";
import ProjectLinkManager from '../components/ProjectLinkManager'
import MultiPickerFluent from "../components/MultiPicker";
import { Chip } from "./ProposalDashboard";
import { styles as myStyles } from './ProposalDashboard';

const styles = mergeStyleSets({
  field: { border: '1px solid var(--neutralSecondary,#605e5c)', borderRadius: 2, padding: 8 },
  header: { marginBottom: 8 },
  smallNote: { opacity: .7 }
});

export default function ProposalsManager(props) {
  // table state
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 1, sort: "year", order: "desc", search: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [filterClients, setFilterClients] = useState([]); // client id
  const [filterPeople, setFilterPeople] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [filterYear, setFilterYear] = useState("");
  const [filterType, setFilterType] = useState("");

  // clients lookup
  const [clients, setClients] = useState([]); // {key:id, text:name}
  const [people, setPeople] = useState([]);
  const [tags, setTags] = useState([]);
  const clientTextById = useMemo(() => Object.fromEntries(clients.map((c) => [c.key, c.text])), [clients]);

  // panel/form state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("create");
  const [form, setForm] = useState(emptyProposal());

  // delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, title}

  // toast
  const [toast, setToast] = useState(null); // {type, text}

  // columns
  const columns = useMemo(() => {
    const onColumnClick = (ev, col) => {
      if (!col) return;
      const isSame = meta.sort === col.fieldName;
      const nextOrder = isSame && meta.order === "asc" ? "desc" : "asc";
      setMeta((m) => ({ ...m, sort: col.fieldName, order: nextOrder }));
    };
    return [
      {
        key: 'title',
        name: 'Title',
        minWidth: 120,
        isResizable: true,
        onRender: (item) => {
          const text = item.title || `Proposal ${item.id}`;
          return (
            <Link to={`/proposals/${item.id}`} styles={{ root: { overflowWrap: 'anywhere' } }}>
              {text}
            </Link>
          );
        },
      },
      {
        key: "client_id",
        name: "Client",
        fieldName: "client_id",
        minWidth: 100,
        isResizable: true,
        onRender: (i) => (
          <Link to={`/clients/${i.client_id}`}>
            {clientTextById[i.client_id] || "—"}
          </Link>
        ),
        // not sortable by backend (omit onColumnClick)
      },
      {
        key: 'ppl',
        name: "Staff",
        fieldName: "staff",
        onRender: ({staff=[]}) => {
          const personas = staff.map(s => ({key: s.id, name: s.name, ...s}));
          return <Stack wrap horizontal tokens={{childrenGap: 2}}>
            {
              personas.map(p => (
                <Stack.Item key={p.id}>
                  <PersonaCoin 
                    onClick={() => setFilterPeople(prev => prev.includes(p.id) ? prev.filter(el => el !== p.id) : prev.concat(p.id))} 
                    style={{cursor: "pointer"}} 
                    styles={{coin: {display: 'inline-block'}}} 
                    coinSize={24} 
                    text={p.name} />
                </Stack.Item>
              ))
            }
          </Stack>
        }
      },
      {
        key: "tags",
        name: "Tags",
        minWidth: 200,
        onRender: ({tags=[]}) => {
          return <Stack horizontal wrap tokens={{childrenGap: 4}}>
            {tags.map(t => <Chip key={t.id} onClick={() => setFilterTags(prev => prev.includes(t.id) ? prev.filter(el => el !== t.id) : prev.concat(t.id))}>{t.name}</Chip>)}
          </Stack>
        }
      },
      { key: "proposal_type", name: "Type", fieldName: "proposal_type", minWidth: 70, isResizable: true, onColumnClick },
      { key: "year", name: "Year", fieldName: "year", minWidth: 70, isResizable: true, onColumnClick },
    ];
  }, [meta.sort, meta.order, clientTextById]);

  // fetch clients for dropdown
  const loadClients = useCallback(async () => {
    try {
      const res = await axios.get("/api/clients", { params: { limit: 1000, page: 1, sort: "name", order: "asc" } });
      const opts = (res.data?.data || []).map((c) => ({ key: c.id, text: c.name, ...c }));
      setClients(opts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  // fetch clients for dropdown
  const loadPeople = useCallback(async () => {
    try {
      const res = await axios.get("/api/people", { params: { active: true, limit: 1000, page: 1, sort: "name", order: "asc" } });
      const opts = (res.data?.data || []).map((p) => ({ key: p.id, text: p.name, ...p }));
      setPeople(opts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const loadTags = useCallback(async () => {
    try {
      const res = await axios.get("/api/tags");
      const opts = (res.data || []).map(t => ({key: t.id, ...t}));
      setTags(opts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  // fetch proposals list
  const fetchProposals = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: overrides.page ?? meta.page,
        limit: overrides.limit ?? meta.limit,
        sort: overrides.sort ?? meta.sort,
        order: overrides.order ?? meta.order,
        search: overrides.search ?? debouncedSearch,
      };
      if (filterClients.length) params.client_id = filterClients.join("&&");
      if (filterYear) params.year = filterYear;
      if (filterType) params.proposal_type = filterType;
      if (filterPeople.length) params.staff = filterPeople.join("&&");
      if (filterTags.length) params.tags = filterTags.join("&&");

      const res = await axios.get("/api/proposals", { params });

      setItems(res.data?.data || []);
      setMeta(
        res.data?.meta || { page: 1, limit: 10, total: 0, totalPages: 1, sort: "year", order: "desc", search: "" }
      );
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to load proposals");
    } finally {
      setLoading(false);
    }
  }, [meta.page, meta.limit, meta.sort, meta.order, debouncedSearch, filterClients, filterYear, filterType, filterPeople, filterTags]);

  useEffect(() => { loadClients(); }, [loadClients]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { loadTags(); }, [loadTags]);
  useEffect(() => { fetchProposals({ page: 1 }); }, [debouncedSearch, filterClients, filterYear, filterType, filterPeople, filterTags]);
  useEffect(() => { fetchProposals(); }, [meta.page, meta.limit, meta.sort, meta.order]);


  async function saveForm() {
    try {
      if (!form.title?.trim()) {
        setToast({ type: MessageBarType.error, text: "Title is required" });
        return;
      }
      const payload = normalizeProposalPayload(form);
      if (panelMode === "create") {
        const res = await axios.post("/api/proposals", payload);
        setToast({ type: MessageBarType.success, text: `Created ${res.data.title}` });
      } else {
        const res = await axios.patch(`/api/proposals/${form.id}`, payload);
        setToast({ type: MessageBarType.success, text: `Updated ${res.data.title}` });
      }
      setPanelOpen(false);
      await fetchProposals();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Save failed" });
    }
  }

  async function confirmDelete() {
    try {
      await axios.delete(`/api/proposals/${deleteTarget.id}`);
      setToast({ type: MessageBarType.success, text: `Deleted ${deleteTarget.title}` });
      setDeleteTarget(null);
      await fetchProposals();
    } catch (e) {
      setToast({ type: MessageBarType.error, text: e?.response?.data?.error || e.message || "Delete failed" });
    }
  }

  return (
    <Stack tokens={{ childrenGap: 12 }} className={myStyles.root}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="end" tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 6 }}>
          <Text variant="xxLarge">Proposals</Text>
          <Text variant="small">Search the proposal database.</Text>
        </Stack>
        <ActionButton iconProps={{ iconName: "Add" }} text="New proposal" onClick={() => props.history.push('/proposals/create')} />
      </Stack>

      {/* Filters */}
      <Stack className={myStyles.sectionCard}>
        <Stack horizontal tokens={{ childrenGap: 12 }}>
          <Stack.Item grow={4}>
            <TextField 
              label="Search" 
              value={search} 
              onChange={(_, v) => setSearch(v || "")} 
              placeholder="Title or full-text" />
          </Stack.Item>
          <Stack.Item grow={1}>
            <TextField 
              label="Year" 
              value={filterYear} 
              onChange={(_, v) => setFilterYear(v.replace(/[^0-9]/g, ""))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <Dropdown 
              label="Type" 
              selectedKey={filterType} 
              options={[
                {key: '', text: 'Any'}, 
                {key: 'RFP', text: "RFP"}, 
                {key: 'RFQ', text: "RFQ"}, 
                {key: 'SOL', text: "SOL"}
              ]} 
              onChange={(e, o) => setFilterType(o.key || "")} />
          </Stack.Item>

          
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 12 }}>
          <Stack.Item grow={1}>
            <MultiPickerFluent
              options={clients}
              label="Clients"
              selected={filterClients}
              searchKey="text"
              onAdd={(item) => setFilterClients(prev => prev.concat(item.id))} 
              onRemove={(item) => setFilterClients(prev => prev.filter(id => id !== item.id))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <MultiPickerFluent 
              options={people}
              label="Staff"
              selected={filterPeople}
              onRenderItem={(item) => <Persona key={item.id} imageUrl={item.image_url} text={item.name} />}
              searchKey="name"
              onAdd={(item) => setFilterPeople(prev => prev.concat(item.id))}
              onRemove={(item) => setFilterPeople(prev => prev.filter(el => el !== item.id))} />
          </Stack.Item>
          <Stack.Item grow={1}>
            <MultiPickerFluent 
              options={tags}
              label="Tags"
              selected={filterTags}
              searchKey="name"
              onAdd={(item) => setFilterTags(prev => prev.concat(item.id))}
              onRemove={(item) => setFilterTags(prev => prev.filter(el => el !== item.id))} />
          </Stack.Item>
          
          <Stack.Item align="end">
            <DefaultButton primary text="Apply" onClick={() => fetchProposals({ page: 1, search })} />
          </Stack.Item>

        </Stack>
      </Stack>

      {toast && (
        <MessageBar messageBarType={toast.type} isMultiline={false} onDismiss={() => setToast(null)}>
          {toast.text}
        </MessageBar>
      )}

      {error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false} onDismiss={() => setError("")}>{error}</MessageBar>
      )}

      <div style={myStyles.sectionCard}>
        {loading ? (
          <Stack horizontal horizontalAlign="center" styles={{ root: { padding: 40 } }}>
            <Spinner label="Loading proposals…" />
          </Stack>
        ) : (
          <DetailsList
            items={items}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        )}
      </div>

      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="small">Page {meta.page} of {meta.totalPages} · {meta.total} total</Text>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <ActionButton text="Prev" disabled={meta.page <= 1} onClick={() => setMeta((m) => ({ ...m, page: Math.max(1, m.page - 1) }))} />
          <ActionButton text="Next" disabled={meta.page >= meta.totalPages} onClick={() => setMeta((m) => ({ ...m, page: Math.min(m.totalPages, m.page + 1) }))} />
        </Stack>
      </Stack>

      {/* Create / Edit Panel */}
      <Panel
        isOpen={panelOpen}
        onDismiss={() => setPanelOpen(false)}
        type={PanelType.large}
        headerText={panelMode === "create" ? "New proposal" : `Edit: ${form.title || "(no title)"}`}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <RequiredTextField label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />

          <TagManager createMode={panelMode === 'create'} entityType="proposals" entityId={form.id} />

          <div className={styles.field}>
            <PeopleManager entityType='proposal' entityId={form.id} />
          </div>

          <div className={styles.field}>
            <ProjectLinkManager entityType='proposal' entityId={form.id} />
          </div>

          <Dropdown
            label="Client"
            placeholder="Select a client"
            options={clients}
            selectedKey={form.client_id || null}
            onChange={(_, opt) => setForm((f) => ({ ...f, client_id: opt?.key || null }))}
          />

          <TextField label="Proposal type" value={form.proposal_type || ""} onChange={(_, v) => setForm((f) => ({ ...f, proposal_type: v || null }))} />
          <TextField label="Year" value={form.year ?? ""} onChange={(_, v) => setForm((f) => ({ ...f, year: v.replace(/[^0-9]/g, "") || null }))} styles={{ root: { width: 160 } }} />
          <TextField label="SharePoint URL" value={form.sharepoint_url || ""} onChange={(_, v) => setForm((f) => ({ ...f, sharepoint_url: v || null }))} />

          <TextField label="Budget" multiline autoAdjustHeight value={form.budget ?? ""} onChange={(_, v) => setForm((f) => ({ ...f, budget: v === "" ? null : v }))} />
          <TextField label="Summary" multiline autoAdjustHeight value={form.summary || ""} onChange={(_, v) => setForm((f) => ({ ...f, summary: v || null }))} />
          <TextField label="About" multiline autoAdjustHeight value={form.about || ""} onChange={(_, v) => setForm((f) => ({ ...f, about: v || null }))} />
          <TextField label="Team" multiline autoAdjustHeight value={form.team || ""} onChange={(_, v) => setForm((f) => ({ ...f, team: v || null }))} />
          <TextField label="Bios" multiline autoAdjustHeight value={form.bios || ""} onChange={(_, v) => setForm((f) => ({ ...f, bios: v || null }))} />
          <TextField label="Approach" multiline autoAdjustHeight value={form.approach || ""} onChange={(_, v) => setForm((f) => ({ ...f, approach: v || null }))} />
          <TextField label="Workplan" multiline autoAdjustHeight value={form.workplan || ""} onChange={(_, v) => setForm((f) => ({ ...f, workplan: v || null }))} />
          <TextField label="Canonical summary" multiline autoAdjustHeight value={form.canonical_summary || ""} onChange={(_, v) => setForm((f) => ({ ...f, canonical_summary: v || null }))} />
          <TextField label="Search text" multiline autoAdjustHeight value={form.search_text || ""} onChange={(_, v) => setForm((f) => ({ ...f, search_text: v || null }))} />

          <Separator />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton text={panelMode === "create" ? "Create" : "Save changes"} onClick={saveForm} />
            <DefaultButton text="Cancel" onClick={() => setPanelOpen(false)} />
          </Stack>
        </Stack>
      </Panel>

      {/* Delete confirm */}
      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => setDeleteTarget(null)}
        dialogContentProps={{ type: DialogType.largeHeader, title: "Delete proposal", subText: deleteTarget ? `Delete ${deleteTarget.title}? This cannot be undone.` : "" }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDelete} text="Delete" />
          <DefaultButton onClick={() => setDeleteTarget(null)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

// ===== helpers =====
function emptyProposal() {
  return {
    id: undefined,
    title: "",
    client_id: null,
    summary: null,
    proposal_type: null,
    year: null,
    about: null,
    team: null,
    bios: null,
    approach: null,
    workplan: null,
    budget: null,
    sharepoint_url: null,
    canonical_summary: null,
    search_text: null,
  };
}

function useDebouncedValue(value, delayMs) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

function normalizeProposalPayload(f) {
  const payload = { ...f };
  // Empty strings -> null, numbers for year
  Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
  if (typeof payload.year === "string" && payload.year !== "") payload.year = Number(payload.year);
  return payload;
}

function RequiredTextField({ label, value, onChange }) {
  const [touched, setTouched] = useState(false);
  const errorMessage = touched && !String(value || "").trim() ? `${label} is required` : undefined;
  return (
    <TextField required label={label} value={value} onChange={(_, v) => onChange(v || "")} onBlur={() => setTouched(true)} errorMessage={errorMessage} />
  );
}